/**
 * OutcomeScaleCard.tsx — one outcome scale's group result (VER-109).
 *
 * Renders, for a single scale (e.g. WHO-5, Stirling), the server-computed report:
 * a plain-language summary, the baseline-vs-endpoint descriptives, the paired t-test
 * and Cohen's d, a group-means chart, and pairing counts. GROUP-LEVEL ONLY — no
 * individual participant data is shown here (that lives only in the CSV export).
 */
import React, { FC } from "react";
import {
    Alert,
    Box,
    Chip,
    Divider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { TrendingDownOutlined, TrendingFlatOutlined, TrendingUpOutlined } from "@mui/icons-material";
import { VCard } from "../../../core/components/VCard";
import { ProgrammeOutcomeReport } from "../../../client/api/survey";
import { PrePostMeansChart } from "./PrePostMeansChart";
import {
    changeDirection,
    descriptivesRows,
    fmtD,
    fmtNum,
    fmtP,
    fmtT,
    isAnonymitySuppressed,
    isSignificant,
    reportableGroupFloor,
} from "./reportFormat";

type OutcomeScaleCardProps = {
    report: ProgrammeOutcomeReport;
};

const DirectionChip: FC<{ report: ProgrammeOutcomeReport }> = ({ report }) => {
    const direction = changeDirection(report);
    if (direction === "unknown") return null;

    // Colour signals a real effect only when the change is statistically significant;
    // a non-significant drift is muted (neutral) so it doesn't overstate.
    const significant = isSignificant(report);
    const config = {
        improved: {
            label: "Improved",
            color: significant ? ("success" as const) : ("default" as const),
            icon: <TrendingUpOutlined fontSize="small" />,
        },
        declined: {
            label: "Declined",
            color: significant ? ("error" as const) : ("default" as const),
            icon: <TrendingDownOutlined fontSize="small" />,
        },
        unchanged: { label: "No change", color: "default" as const, icon: <TrendingFlatOutlined fontSize="small" /> },
    }[direction];

    return <Chip size="small" color={config.color} icon={config.icon} label={config.label} />;
};

const ScaleHeader: FC<{ report: ProgrammeOutcomeReport; showChip: boolean }> = ({ report, showChip }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
        <Box>
            <Typography variant="overline" color="textSecondary">
                {report.scaleCode} · {report.higherIsBetter ? "higher is better" : "lower is better"}
            </Typography>
            <Typography variant="h6">{report.scaleName}</Typography>
        </Box>
        {showChip && <DirectionChip report={report} />}
    </Stack>
);

export const OutcomeScaleCard: FC<OutcomeScaleCardProps> = ({ report }) => {
    const { scaleName, baseline, endpoint, tTest, effectSize } = report;
    const significant = isSignificant(report);

    // ANONYMITY: below the group-size floor the numbers approach individual scores, so
    // suppress everything except the count. No summary/chart/table — those can carry an
    // individual's data at very small n. (Individual data lives only in the CSV export.)
    if (isAnonymitySuppressed(report)) {
        return (
            <VCard variant="outlined" sx={{ mb: 3 }}>
                <ScaleHeader report={report} showChip={false} />
                <Alert severity="info">
                    Group results for this scale are hidden to protect participant anonymity — only{" "}
                    {report.participantsPaired} participant{report.participantsPaired === 1 ? "" : "s"} had both a
                    baseline and a later measurement, below the reporting threshold of {reportableGroupFloor(report)}.
                    The detailed data is still available in the raw CSV export.
                </Alert>
            </VCard>
        );
    }

    return (
        <VCard variant="outlined" sx={{ mb: 3 }}>
            {/* Header: scale + polarity + direction */}
            <ScaleHeader report={report} showChip={true} />

            {/* Plain-language summary — the authoritative, non-diagnostic read-out */}
            <Typography variant="body1" sx={{ mb: 2 }}>
                {report.summary}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                {/* Descriptives + inferential stats */}
                <Box flex={1} minWidth={0}>
                    <Table size="small" aria-label={`${scaleName} descriptive statistics`}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Statistic</TableCell>
                                <TableCell align="right">Baseline</TableCell>
                                <TableCell align="right">Endpoint</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {descriptivesRows(baseline, endpoint).map((row) => (
                                <TableRow key={row.label}>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell align="right">{row.baseline}</TableCell>
                                    <TableCell align="right">{row.endpoint}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Paired-samples t-test (first vs last administration)
                        </Typography>
                        {tTest.computable ? (
                            <Typography variant="body2" color="textSecondary">
                                Mean change {fmtNum(tTest.meanDiff)} · t({tTest.df}) = {fmtT(tTest.t)} · p ={" "}
                                {fmtP(tTest.p)}
                                {significant ? " (statistically significant)" : " (not significant)"}
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                {tTest.note ?? "Not computable for this group."}
                            </Typography>
                        )}
                        {effectSize.computable ? (
                            <Typography variant="body2" color="textSecondary">
                                Effect size (Cohen&apos;s d) = {fmtD(effectSize.d)} ({effectSize.magnitude})
                            </Typography>
                        ) : (
                            effectSize.note && (
                                <Typography variant="body2" color="textSecondary">
                                    Effect size: {effectSize.note}
                                </Typography>
                            )
                        )}
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            {report.participantsPaired} of {report.participantsWithData} participants had both a
                            baseline and an endpoint measurement and could be paired.
                        </Typography>
                    </Box>
                </Box>

                {/* Group-means chart */}
                <Box flex={1} minWidth={0}>
                    <PrePostMeansChart baseline={baseline} endpoint={endpoint} />
                </Box>
            </Stack>
        </VCard>
    );
};
